export const LINK = "‿"; // chaining symbol (connect words, no pause)

export const EXERCISES = [
// 1A
{
  id: "1A",
  patterns: ["XxXx", "XX", "XXx"],

  examples: [
    "Kari snakker",
    "Jenta synger",
    "Tor reiser"
  ],

  studentCore: [
    `KAri${LINK}SNAkker`,
    `JENta${LINK}SYNger`,
    `TOR${LINK}REIser`
  ],

  extraSpecific: [
    "S-cluster trick: ‘Kari snakker’ → ‘Karis-nakker’ (move /s/ to the previous syllable in the sound chain).",
    "Use this for initial two-consonant clusters that start with /s/ (sn-, sp-, st-, sk-, etc.).",
    "No pauses between feet / across the boundary — keep one continuous chain.",
    "<ng> in ‘synger’ is usually one sound [ŋ]; some dialects use [ŋg] (teacher chooses the target variant)."
  ],

  teacher: {
    focus: [
      "Exercise contains two foot types: Xx and X.",
      "Main opening structure: XxXx.",
      "Some utterances form XX (two one-syllable feet).",
      "Others form XXx (first foot one syllable, second two).",
      "Students must mark the start of each foot clearly.",
      "Both feet must form one continuous chain without pause.",
      "First six utterances have vowel → consonant linking between feet.",
      "The vowel glides directly into the next consonant."
    ],

    articulation: [
      "Learners may insert a glottal stop + vowel before an initial /s/+cluster (‘snakker’).",
      "Solve by moving /s/ to the previous syllable in the sound chain: ‘Karis-nakker’.",
      "This applies to initial /s/+consonant clusters (as in several items in 1A).",
      "Where identical consonants meet across the boundary (‘Tor reiser’), no pause is allowed.",
      "The final consonant slides directly into the next word (one long consonant, not two).",
      "<ng> is normally [ŋ]; some dialects use [ŋg].",
      "Teacher chooses the target variant."
    ],

    actions: [
      "Chain drill: clap only on stressed syllables.",
      "S-cluster repair drill using the ‘Karis-nakker’ pattern.",
      "Glide exercise: exaggerate vowel→consonant linking.",
      "Retrofit errors immediately; do not let pauses fossilize.",

      // Extra exercises
      "Extra: use Øving 1 sentences as answers to name-questions.",
      "Questions = personal names with question intonation.",
      "Maintain vowel length in both question and answer.",
      "Students ask each other parallel questions."
    ]
  }
},

// 1B
{
  id: "1B",
  patterns: ["XxXx", "XxX", "XX"],
  examples: ["Snakker Kari?", "Leser jenta?", "Går Liv?"],

  studentCore: [
    `SNAkker${LINK}KAri?`,
    `LEser${LINK}JENta?`,
    `GÅR${LINK}LIV?`,
  ],

  extraSpecific: [
    "Yes/no questions: the verb comes first. Don’t over-focus on grammar; focus on rhythm and fluency.",
    "All verbs are present tense, so they typically end in /r/. Keep final /r/ linked into the next word — no pause.",
    "Apical /r/ dialects (most of Norway except parts of the Southwest): /r/ + /l/ may merge to a retroflex [ɭ], and /r/ + /t/ may merge to a retroflex [ʈ].",
    "Schwa deletion: /e/ may drop in weak syllables between /t d s l n/ and /n/ (e.g., gutten, boden, fossen, halen, vinen).",
    "Some learners insert a vowel (and sometimes a glottal stop) before word-initial /s/+consonant clusters (sn-, sp-, st-, sk-), e.g. *[ʔə s n…].",
    "If that happens, go back to 1A and practice in contexts where /s/ can be re-attached leftward in the sound chain; that kind of ‘splitting’ is not used in 1B.",
    "Alternative fix for /sC/: hold a very long /s/ (a couple of seconds), then slide into the next consonant. Repeat; then shorten /s/ and increase speed. Revisit often — the skill fades quickly.",
    "Because pronunciation is the goal, you do not need to emphasize the grammar rule V–S (Verb–Subject) beyond what students need to read the question correctly.",
  ],

  teacher: {
    focus: [
      "Same language material as 1A, but now as questions.",
      "Rhythm patterns practiced here: XxXx, XxX (new), and XX.",
      "Yes/no questions → verb first; present tense verbs typically end in /r/ and must be chained smoothly into the next word.",
      "In items where the second foot begins with a dental, apical-/r/ dialects often show retroflex assimilation: /r/+ /l/ → [ɭ], /r/+ /t/ → [ʈ].",
      "Watch for schwa loss in weak syllables in the environment described (…t/d/s/l/n… + n).",
      "Monitor /s/+consonant clusters: prevent epenthetic vowel/glottal onset before the cluster; if needed, train with long /s/ → slide → shorten.",
      "Keep rhythm central; the whole question should be one continuous chain.",
    ],

    actions: [
      "Rhythm drill: students clap on each strong beat (X) while saying the whole question without breaks.",
      "Verb-first chaining drill: repeat short questions (verb + subject) and enforce zero pause after the verb-final /r/.",
      "Retroflex awareness (dialect-appropriate): model a couple of /r/+dental cases; students imitate, but don’t overcorrect.",
      "Schwa-drop spotting: teacher reads two versions (with/without weak /e/); students identify both as acceptable Norwegian reductions when rhythm stays intact.",
      "S-cluster intervention: long /s/ (1–2 seconds) → slide into next consonant → repeat → shorten /s/ → increase tempo. Revisit regularly or it fades.",
      "Extra exercises: continue with Xx questions like “LESer Kari?”.",
      "Answers add stressed NEI: “NEI, KAri‿SNAkker” → pattern X, XxXx.",
      "Communicative extension: use pictures/actions; students invent new verbs but keep the same rhythm patterns.",
    ],
  }
},



  // 2A
 {
  id: "2A",
  patterns: ["xXx", "xX (special case)"],
  examples: ["Hun staver", "han skriver"],
  studentCore: [`hun${LINK}STAver`, `han${LINK}SKRIver`],

  extraSpecific: [
    "Anacrusis = a weak syllable before the first strong beat. Say it FAST with a flat tone.",
    "You should clearly hear the difference between ‘han’ and ‘Ann’: in ‘Han leser’ the first word is a pickup; in ‘Ann leser’ it is a full foot.",
    "Key rule: the last sound of the anacrusis (often /n/) must flow straight into the next sound — no stop.",
    "If you insert a tiny ‘uh’ or a glottal start before sn-/sp-/st-/sk-/sm- (e.g. *e-snakker*): fix it by attaching [s] to the final [n] of the previous word (Hans_nakker, huns_staver).",
    "Optional assimilation: before /g/, /n/ may be pronounced [ŋ] (ng-sound). This is allowed and can make speech smoother."
  ],

  teacher: {
    focus: [
      "This exercise introduces anacrusis: a weak syllable before the first stressed syllable. Target patterns are xXx and one case of xX.",
      "Anacrusis must be produced quickly with relatively flat intonation (no new melodic event before the beat).",
      "Students should hear the contrast between a pickup (‘han’) and a full foot (‘Ann’).",
      "Boundary rule: the final /n/ of the anacrusis must connect directly into the next sound.",
      "In 2A1–11 the /n/ goes into a consonant.",
      "If that consonant begins an /s/+cluster (snakker, spiser, staver, svarer, smiler), learners may stop and insert [ʔə].",
      "Repair strategy: re-syllabify in the stream — attach [s] to the final [n] of the previous word (Hans_nakker, huns_staver…).",
      "In 2A12 the transition is vowel → /s/ (‘De snakker’), which must also stay continuous.",
      "Special assimilation example: /n/ → [ŋ] before /g/ (e.g., ‘hun går’).",
      "This assimilation is optional but reduces articulatory movement and improves fluency because both [ŋ] and [g] are velar."
    ],

    actions: [
      "Demonstrate the tonal target: model the pickup word (hun/han) fast + flat, then land the beat on the verb.",
      "Contrast drill: alternate ‘Han leser’ vs ‘Ann leser’ so students hear pickup vs foot.",
      "Clap only on the verb (STA-/SKRI-). No clap for hun/han.",
      "Chain drill: repeat ‘hun‿STAver’ 5–10 times; then swap verbs to generalize (hun‿SKRIver / han‿STAver…).",
      "Anti-epenthesis drill for /sC/: lengthen the final [n] in the pickup → slide into [s] (…nssss…) → continue into the word → shorten gradually.",
      "Assimilation awareness: show both [n g] and [ŋ g]; allow either, explain that [ŋ] is an articulatory shortcut.",
      "Extra exercise (from the guide): reuse 1A questions, but answers now follow xXx.",
      "Example: ‘Hun snakker’ = xXx. The first word must be fast and flat.",
      "Teacher must explicitly model the tonal contour before students repeat."
    ]
  }
}

,

  // 2B
  {
  id: "2B",
  patterns: ["Xxx (main)", "Xx (2B9 exception)"],
  examples: ["Staver hun?", "Skriver han?"],
  studentCore: [`STAver${LINK}hun?`, `SKRIver${LINK}han?`],

  extraSpecific: [
    "New rhythm: two weak syllables after the beat (Xxx). Keep them light and fast.",
    "After /r/, /h/ may weaken or disappear in fast speech: ‘snakker hun’ can sound like snakker_un. This is normal — keep the chain connected.",
    "The entire question must remain one continuous sound wave. No breaks.",
    "Intonation pattern must stay intact while extending the foot."
  ],

  teacher: {
    focus: [
      "Students previously trained Xx feet; this exercise maintains and extends rhythmic control to Xxx.",
      "Two light syllables now follow the stressed beat; they must remain fast and unstressed.",
      "Watch /h/ after /r/: in this environment /h/ may drop naturally in fluent speech.",
      "Do not force hyper-articulation if fluency improves without /h/.",
      "2B11: /r/ + /d/ assimilates to a retroflex sound in most apical-/r/ dialects.",
      "Accept dialectal variation but keep the chain continuous.",
      "Intonation pattern must remain stable across the entire utterance."
    ],

    actions: [
      "Rhythm extension drill: clap only on the stressed syllable; whisper the two weak syllables.",
      "r+h smoothing drill: model both careful and fluent versions (‘snakker hun’ vs ‘snakker_un’); students imitate the fluent chain.",
      "Retroflex awareness: demonstrate r+d assimilation briefly; do not overteach — prioritize flow.",
      "Chain integrity drill: stop immediately if a pause appears inside the foot.",

      // Extra exercises from guide
      "Extra exercise 1: answers begin with stressed ‘Ja’, which forms its own foot.",
      "Rising intonation naturally occurs on ‘Ja’.",
      "After a short pause comes a one-syllable pickup + a two-syllable foot → X, xXx.",
      "Students answer: ‘Ja, hun STAver’ with the same rhythm.",
      "Extra exercise 2: replace the main verb with ‘gjøre’.",
      "This may require a brief grammar explanation (parallel to English ‘do’ if helpful).",
      "If grammar becomes distracting, postpone the exercise.",
      "All responses share the same rhythm: X, xXx.",
      "Pair work: students circulate asking and answering.",
      "If students know additional verbs, substitute them.",
      "Mark stress on the board if needed."
    ]
  }
}

,

  // 3A
  {
  id: "3A",
  patterns: ["XxXxxx (target)", "XxXxXx (allowed but NOT goal)"],
  examples: ["Grete staver ikke", "Rita teller ikke"],
  studentCore: [
    `GREte${LINK}STAver${LINK}ikke`,
    `RIta${LINK}TELler${LINK}ikke`
  ],

  extraSpecific: [
    "Long feet may feel unstable. Do not split them into smaller beats — keep the long wave.",
    "Final /r/ of the verb must glide directly into the initial /ɪ/ of ‘ikke’. No gap.",
    "The final vowel in ‘ikke’ must stay central and relaxed (a weak ‘e’).",
    "Aspirated stops must remain aspirated — rhythm must not remove aspiration.",
    "Long vowels must remain long.",
    "If /s/-clusters are difficult, attach /s/ to the previous word (s-piser chaining)."
  ],

  teacher: {
    focus: [
      "This exercise negates earlier sentence types from 1A.",
      "Target rhythm: XxXxxx. First foot Xx is familiar; second foot Xxxx is new and long.",
      "Students may shorten the long foot to XxXxXx. This is acceptable in Norwegian, but NOT the goal here.",
      "Presens verbs end in /r/ and must connect directly to the initial /ɪ/ of ‘ikke’.",
      "The final vowel in ‘ikke’ should be central (‘slack e’), not over-articulated.",
      "Imitation of the foot pattern is more important than spelling accuracy.",
      "Aspirated plosives must stay aspirated (kʰ, tʰ etc.).",
      "Long vowels must preserve length even under rhythmic compression.",
      "Important Norwegian feature: final cluster in ‘gutten’ without vowel nucleus [ˈɡʉtːn̩].",
      "Mastery of this cluster signals strong Norwegian articulatory control.",
      "If initial /s/-clusters cause epenthesis, reattach /s/ to the previous word."
    ],

    actions: [
      "Long-foot drill: teacher exaggerates the long second foot; students echo without inserting extra beats.",
      "Verb→ikke chaining loop: isolate ‘-ver‿ikke’ / ‘-ler‿ikke’ and repeat rapidly.",
      "Aspiration check: hold paper/tissue to feel airflow on kʰ/tʰ.",
      "Vowel-length contrast drill: alternate long vs short vowels while rhythm stays constant.",
      "Final cluster shortcut: ‘Gutten nikker’ → ‘Gutt nikker’ → ‘Gutt n’. Ensure /t/ and /n/ stay chained.",
      "S-cluster repair: move /s/ to previous word (s-piser method).",

      // Extra exercises from guide
      "Extra exercise: questions contain a one-syllable anacrusis → xXx.",
      "Answers follow the 2A structure → xX.",
      "Introduce verb substitution with ‘gjøre’.",
      "In questions the verb carries stress: ‘Hva GJØR hun?’",
      "/h/ in ‘hun’ is weak and often absent in natural speech.",
      "Teacher decides whether to explain the grammar or teach as a fixed phrase.",
      "Students must notice that <gj> represents a single sound /j/."
    ]
  }
}

,

  // 3B
{
  id: "3B",
  patterns: ["XxxxXx (long foot first)"],
  examples: ["Staver ikke Grete?", "Teller ikke Rita?"],
  studentCore: [
    `STAver${LINK}ikke${LINK}GREte?`,
    `TELler${LINK}ikke${LINK}RIta?`
  ],

  extraSpecific: [
    "The long foot comes first. Do not split it — keep one continuous wave.",
    "Final /r/ of the verb must link directly into ‘ikke’. No reset.",
    "If a consonant follows ‘ikke’, the final vowel of ‘ikke’ must attach to that consonant.",
    "When two vowels meet across word boundaries (‘ikke Anne’), link them smoothly — never insert a glottal stop."
  ],

  teacher: {
    focus: [
      "As in earlier B-exercises: declarative → question transformation.",
      "Target rhythm: XxxxXx (long foot followed by short foot).",
      "Verb-final /r/ must chain directly into ‘ikke’.",
      "If the next word begins with a consonant, the final vowel of ‘ikke’ must link to it (e.g., ‘kommer‿ikke‿Kari’).",
      "Hiatus = two adjacent vowels belonging to different syllables. Norwegian links them smoothly.",
      "Some languages forbid hiatus and insert a glottal stop. This must be avoided in Norwegian.",
      "Explicitly train vowel-to-vowel chaining across word boundaries.",
      "3B11: ‘barna’ may be pronounced /rn/ or retroflex nasal [ɳ] depending on dialect. Accept both."
    ],

    actions: [
      "Long-foot-first drill: teacher exaggerates the long first foot; students echo without inserting extra beats.",
      "Verb→ikke chaining loop: isolate ‘-ver‿ikke’ / ‘-ler‿ikke’ and repeat rhythmically.",
      "Hiatus drill: practice vowel-to-vowel linking (‘ikke‿Anne’) slowly → faster → natural tempo.",
      "Wave drill: students trace a long hand wave for the first foot, short tap for the second.",

      // Extra exercises: numbers
      "Numbers pronunciation drill: review counting with focus on vowel length.",
      "Watch difficult sounds for some learners: /ʃ/ and /ʉ/.",
      "‘elleve’: middle vowel drops → [ˈɛlːve].",
      "‘tolv’: final /v/ normally disappears → [tʰɔlː].",
      "Counting rhythm: each number = one foot.",
      "Then add weak connectors: ‘en-og-to-og-tre-og’.",
      "Practical counting tasks: count students, objects, groups, genders — integrate rhythm into real situations."
    ]
  }
}

,

  // 4A
  {
  id: "4A",
  patterns: ["xXxxx (anacrusis + 4-syllable foot)"],
  examples: ["Hun staver ikke", "hun teller ikke"],
  studentCore: [
    `hun${LINK}STAver${LINK}ikke`,
    `hun${LINK}TELler${LINK}ikke`
  ],

  extraSpecific: [
    "The anacrusis (first weak syllable) must be fast and light.",
    "Weak syllables inside the long foot must stay light too — do not slow down.",
    "If an /s/+consonant cluster is difficult, attach /s/ to the end of the anacrusis: ‘huns_taver’.",
    "Final /r/ of the verb must connect directly to /i/ in ‘ikke’ — no pause."
  ],

  teacher: {
    focus: [
      "Builds on 3A: sentences begin with a weak pickup syllable (anacrusis). Target rhythm xXxxx.",
      "The foot has four syllables. Long feet often tempt learners to insert extra beats.",
      "Anacrusis must be rapid and light; the weak syllables inside the foot must also remain light.",
      "Watch /s/+consonant initial clusters; move /s/ left into the anacrusis (huns_taver).",
      "Link verb-final /r/ directly into the /i/ of ‘ikke’: ‘taver‿ikke’."
    ],

    actions: [
      "Pickup drill: students whisper ‘hun’, then speak the verb normally; repeat until the pickup stays light.",
      "Foot-integrity drill: clap only on the strong beat; students keep the remaining syllables compressed.",
      "S-cluster repair: ‘hunssss…taver’ → shorten /s/ → natural speed.",
      "Verb→ikke chaining loop: isolate ‘-ver‿ikke / -ler‿ikke’ and repeat rhythmically.",

      // Extra exercises: time
      "Extra (telling time): answers contain two feet → pattern XxxX (except ‘elleve’, which is XxxXx).",
      "The entire time expression must be produced as one rhythmic unit.",
      "Encourage retroflex sounds in dialects where they occur (marked in the guide).",
      "/r/ is handled three ways in these items: pronounced, optionally omitted, or retroflexed (with apical r). Keep classroom variety consistent."
    ]
  }
}

,

  // 4B
  {
  id: "4B",
  patterns: ["Xxxxx (main)", "Xxxx (4B9 exception)"],
  examples: ["Staver hun ikke?", "Teller hun ikke?"],
  studentCore: [
    `STAver${LINK}hun${LINK}ikke?`,
    `TELler${LINK}hun${LINK}ikke?`
  ],

  extraSpecific: [
    "After the first strong beat, the remaining syllables must continue as one uninterrupted chain.",
    "Initial /h/ in the second word is usually weak in natural Norwegian (except one item noted in the guide). Avoid velar [x] (like German ‘Bach’).",
    "Use the final /n/ in han/hun to link directly into vowel-initial ‘ikke’: ‘hun-nikke’. This prevents a glottal start.",
    "If you say *estaver / eskriver*: start with a long SSSS… then slide into the cluster; repeat often.",
    "Aspiration must be audible in words like ‘kommer’ and ‘teller’. After /s/, aspiration is naturally weaker — this is correct."
  ],

  teacher: {
    focus: [
      "Transforms 4A statements into questions. The foot expands to five syllables (Xxxxx); 4B9 has four.",
      "After the initial strong syllable, all following weak syllables must run as a continuous chain.",
      "Initial /h/ in the second word is typically weak (except 4B11, which keeps normal strength). Avoid velar fricative [x].",
      "Link /n/ in ‘han/hun’ directly to the vowel in ‘ikke’. Some languages insert a glottal stop before vowel-initial stressed words — this must be avoided in Norwegian.",
      "Target chaining model: ‘hun-nikke’.",
      "If /sC/ clusters trigger epenthesis (*estaver, eskriver*), repair by prolonging /s/ and sliding into the cluster.",
      "Aspiration must remain clear in 4B1 and 4B5 (‘kommer, teller’). When a plosive follows /s/, reduced aspiration is normal (compare ‘på’ vs ‘spå’)."
    ],

    actions: [
      "Chain drill: clap only on the first strong beat; students speak the rest in one breath.",
      "Weak-h drill: model weak /h/ and silent /h/; accept both if rhythm stays intact, forbid [x].",
      "Anti-glottal drill: exaggerate /n/ bridging (‘hun-nikke’) to suppress glottal onset.",
      "S-cluster repair loop: long /s/ → slide → shorten → increase tempo; revisit regularly.",
      "Aspiration demo: tissue test (‘på’ vs ‘spå’) to make airflow visible.",

      // Extra exercises
      "Extra rhythm: xxXxxx with question-word anacrusis, e.g. ‘Hvilken farge har den?’",
      "Regional /r/: uvular or front trill dialects lack /r/+ /d/ assimilation; apical /r/ dialects may produce retroflex outcomes.",
      "Schwa deletion in ‘den’ is extremely common; model the reduced natural form (and retroflex variants where relevant).",
      "Classroom extension: ask about objects in the room using the color template.",
      "Use simple adjectives (pen, ny, etc.). Avoid neuter/plural adjective agreement until introduced in the textbook."
    ]
  }
}

,

  // 5A
  {
  id: "5A",
  patterns: ["XxxXx (target: ‘vil’ unstressed)", "Alternative stressed ‘vil’ exists but not trained"],

  examples: ["Grete vil rydde", "Rita vil vaske"],

  studentCore: [
    `GREte${LINK}vil${LINK}RYDde`,
    `RIta${LINK}vil${LINK}VASke`
  ],

  extraSpecific: [
    "Future meaning is expressed with the helper ‘vil’ (= will). In this exercise ‘vil’ must stay weak — do NOT stress it.",
    "Subject + vil + verb must form one continuous rhythmic unit.",
    "If you stress ‘vil’, the meaning shifts to strong intention. That pattern exists in Norwegian but is NOT trained here."
  ],

  teacher: {
    focus: [
      "Introduces future via auxiliary ‘vil’ (‘Kari vil komme’). Here ‘vil’ belongs inside the foot that begins with the subject.",
      "Target rhythm: XxxXx. Other rhythmic realizations are possible in Norwegian, but not practiced in this exercise.",
      "Maintain earlier fundamentals: clear stress placement, correct vowel length, aspiration, chaining, and fast weak syllables.",
      "5A9: /v/ in ‘Liv vil’ should be produced as one continuous unit across the boundary.",
      "5A11: watch /r/+ /n/ assimilation in ‘barna’ in dialects where this is relevant.",
      "5A12: <ng> is normally pronounced as a single sound [ŋ]; teacher chooses the consistent class model."
    ],

    actions: [
      "Contrast demo: briefly show weak ‘vil’ (target) vs stressed ‘VIL’ (intent meaning), then return to the target rhythm.",
      "Beat drill: clap on GRE-/RI- and RYD-/VAS-; students must keep ‘vil’ short and unstressed.",
      "Boundary smoothing: isolate ‘Liv‿vil’ and train it as one long /v/ before inserting into full sentences.",
      "Ng consistency drill: model [ŋ] and keep the pronunciation stable for the whole group.",

      // Extra exercises
      "Extra question pattern: ‘Hvilken farge har den?’ with rhythm xxXxxx (question word = anacrusis).",
      "Teach reduced ‘den’: dropping schwa in ‘den’ is the default natural pronunciation.",
      "Answer structure: start with two weak syllables and end with a one-syllable foot: ‘Den er RØD’ → xxX.",
      "Exception: colors like ‘rosa’ and ‘lilla’ produce a two-syllable final foot (Xx).",
      "Classroom extension: ask about real objects (clothes, items). Add adjectives (stor, pen, ny) but avoid neuter/plural agreement until introduced in the textbook.",
      "Repeat later for reinforcement."
    ]
  }
}

,

  // 5B
  {
  id: "5B",
  patterns: ["xXxXx (short anacrusis + two identical feet)"],

  examples: ["Vil Grete rydde?", "Vil Rita vaske?"],

  studentCore: [
    `vil${LINK}GREte${LINK}RYDde?`,
    `vil${LINK}RIta${LINK}VASke?`
  ],

  extraSpecific: [
    "This rhythm is simple: keep the whole question as one smooth unit.",
    "If /s/+consonant clusters are difficult (e.g. ‘skrive’), attach /s/ to the previous word (‘Vil guttens krive’ style repair).",
    "In ‘Vil Liv gå’, pronounce /l/ as one clean sound — avoid heavy English-style dark L.",
    "Velarized L exists in Norwegian after back vowels (Ole, åle, alle), but should not spread everywhere."
  ],

  teacher: {
    focus: [
      "5A statements are converted into questions. Rhythm: short anacrusis + two identical feet → xXxXx.",
      "This exercise is intentionally simple and ideal for chaining the entire utterance into one prosodic unit.",
      "No new phonological features are introduced.",
      "Complex /sC/ clusters (5B7–8) may trigger epenthesis; use /s/ reattachment if needed.",
      "Watch /l/ quality in 5B9 (‘Vil Liv gå’): tongue should stay relatively flat.",
      "Strong velarized (‘dark’) L like English ‘little’ creates accent.",
      "Norwegian does allow velarized L, but mainly after back vowels; keep distribution natural."
    ],

    actions: [
      "Whole-utterance drill: students say the full question in one breath; forbid micro-pauses.",
      "S-cluster repair loop: long /s/ → slide into cluster → shorten /s/ → increase speed.",
      "L-quality contrast drill: teacher models light vs dark L; students imitate the lighter Norwegian version.",

      // Extra exercises
      "Extra Q/A sequences: questions same as 5A; answers follow rhythm X, xXx.",
      "Model answers: ‘Ja, det kan hun’, ‘Ja, det må han’, ‘Ja, det bør de’.",
      "Explain that mastering this pattern contributes directly to conversational fluency.",
      "Use during auxiliary + main verb practice (kan/må/bør + verb)."
    ]
  }
}

,

  // 6A
  {
  id: "6A",
  patterns: ["xxXx (two-syllable anacrusis + 2-syllable foot)"],

  examples: ["Hun kan rydde", "du bør dra"],

  studentCore: [
    `hun${LINK}kan${LINK}RYDde`,
    `du${LINK}bør${LINK}DRA`
  ],

  extraSpecific: [
    "Two weak pickup syllables: say them FAST with flat tone.",
    "Even if the last syllable is written as a separate word, keep it inside the same foot.",
    "Some nasal changes in fast speech are normal; smooth chaining matters more than exact tongue position."
  ],

  teacher: {
    focus: [
      "This exercise introduces a 2-syllable anacrusis: xxXx. The same structure appeared earlier in 5A extras, but is explicit here.",
      "The two weak syllables in the anacrusis must be produced quickly with relatively flat intonation.",
      "In some items the final syllable is written as a separate word; it must still be integrated into the foot, not promoted to a new foot (unless deliberately emphasizing meaning).",
      "In dialect areas with front/apical /r/, some items show assimilation between /r/ and dental consonants.",
      "Optional velar assimilation: final /n/ in ‘han/hun’ may become [ŋ] before velar plosives (‘kan’). This reduces tongue movement and increases fluency.",
      "Optional labiodental [ɱ] or bilabial [m] nasals may appear in fast speech in certain items. These are natural shortcuts.",
      "If this phonetic detail confuses learners, ignore it — a normal dental /n/ is perfectly acceptable.",
      "Item note: only one /r/ should be audible in sequences like ‘bør‿reise’."
    ],

    actions: [
      "Pickup compression drill: teacher counts ‘1-2-CLAP’ where clap = strong beat; students speak the sentence in that timing.",
      "Foot-integration drill: exaggerate the strong syllable; students must keep the final word attached to the foot.",
      "Velar nasal demo: contrast [n k] vs [ŋ k] once; explain that [ŋ] is optional and about efficiency.",
      "Fluency rule: remind students that smooth chaining outranks micro-phonetic precision.",

      // Extra exercises (math rhythm)
      "Extra exercise: math questions (‘Hva er to pluss to?’) with rhythm xxXxX.",
      "Answer pattern: ‘To pluss to er fire’ → XxXxXx.",
      "Students ask and answer math problems in pairs to reinforce chaining and beat structure."
    ]
  }
}

,

  // 6B
  {
  id: "6B",
  patterns: ["xxXx (main)", "xxXxx (6B12 exception)"],

  examples: ["Vil hun komme?", "Må du dra nå?"],

  studentCore: [
    `vil${LINK}hun${LINK}KOMme?`,
    `må${LINK}du${LINK}DRA${LINK}nå?`
  ],

  extraSpecific: [
    "Same rhythm as 6A, now in question form.",
    "Keep the two weak pickup syllables fast and flat.",
    "Optional: /h/ in ‘han/hun’ may disappear in fast speech; rhythm and chaining matter more than spelling."
  ],

  teacher: {
    focus: [
      "Repetition of the 6A rhythm (xxXx) in question form with different auxiliary verbs.",
      "Maintain the rhythm carefully in longer items (especially 6B6).",
      "6B12 contains a 3-syllable foot (xxXxx), but the overall rhythm must still be preserved.",
      "When ‘være’ is emphasized, the utterance may function as a single foot.",
      "Segmental realization should now be familiar; the goal is stable production.",
      "In transcription, /h/ in ‘han/hun’ is often marked as absent in fast speech.",
      "For /n/ + /v/ sequences (‘vil hun være’), see comments under 6A.",
      "Students with serious segmental problems should receive focused individual work."
    ],

    actions: [
      "Question chaining drill: students repeat each question in one breath; teacher monitors pickup compression.",
      "Auxiliary variation drill: substitute helper verbs while preserving identical rhythm.",
      "Optional-h modeling: teacher demonstrates both with and without /h/; accept either if rhythm holds.",
      "Individual repair: isolate persistent sound problems and treat separately.",

      // Extra exercises (answers)
      "Extra exercise: answers follow rhythm X, xXx.",
      "In fast speech the pause after ‘Ja’ may disappear → XxXx.",
      "Rhythm must remain identical regardless of auxiliary verb.",
      "Extend with additional verbs students already know."
    ]
  }
}

,
// 7A

{
  id: "7A",
  patterns: ["XxxxxXx (two feet: long + short)"],
  examples: [
    "Sølvi vil ikke stave",
    "Anne vil ikke synge"
  ],

  studentCore: [
    `SØLvi${LINK}vil${LINK}IKke${LINK}STAve`,
    `ANne${LINK}vil${LINK}IKke${LINK}SYNge`
  ],

  extraSpecific: [
    "Two feet: the first foot is long. The four weak syllables inside it must be very fast and relatively flat in tone.",
    "There must be no pauses anywhere. All words form one continuous sound chain.",
    "Most boundaries are vowel → consonant, which helps chaining.",
    "If an /s/+consonant cluster is difficult (sn-, sp-, st-, sk-, sm-), attach /s/ to the previous word (earlier repair strategy).",
    "One item contains vowel–vowel contact across a word boundary. These vowels must link smoothly without glottal stop.",
    "Long vowels must remain long. Speed must not shorten vowel duration."
  ],

  teacher: {
    focus: [
      "Difficulty increases: two feet are now present. Typical structure: XxxxxXx.",
      "Primary goal: compress the four weak syllables in the first foot while keeping a flat tonal contour.",
      "Transitions must remain smooth. No boundary resets.",
      "Vowel length remains critical. Long vowels must be preserved even under compression.",
      "The chaining strategy from earlier exercises still applies to /s/+clusters.",
      "Vowel–vowel boundaries must remain continuous. Avoid inserting glottal stops.",
      "Students should now execute the long foot as one gesture rather than counting syllables."
    ],

    actions: [
      "Whisper drill: students whisper the weak syllables, speak the stressed syllables normally, then merge back to natural speech.",
      "One-breath rule: entire utterance must be spoken without pause.",
      "Clap drill: clap only on the stressed beats; forbid extra beats.",
      "Vowel audit: isolate long vowels, exaggerate length, reintegrate into full rhythm.",
      "Teacher stops immediately if a micro-pause appears inside the long foot."
    ],

    extraExercises: [
      "Mathematical expressions expand rhythm control.",
      "Words ‘minus’, ‘times’, and ‘divided by’ contain two syllables but are usually weak inside equations.",
      "Model rhythms:",
      "• What is two plus three? → xxXxX",
      "• What is five minus two? → xxXxxX",
      "• What is two times two? → xxXxxX",
      "• What is six divided by two? → xxXxxX",
      "Answers:",
      "• Two plus three is five",
      "• Five minus two is three",
      "• Two times two is four",
      "• Six divided by two is three",
      "Students create their own equations and read them while preserving rhythm."
    ]
  }
}

,
// 7B
{
  id: "7B",
  patterns: ["xxxXxXx (3-syllable anacrusis + two feet)"],
  examples: [
    "Vil ikke Sølvi komme?",
    "Vil ikke gutten skrive?"
  ],

  studentCore: [
    `vil${LINK}ikke${LINK}SØLvi${LINK}KOMme?`,
    `vil${LINK}ikke${LINK}GUTten${LINK}SKRIve?`
  ],

  extraSpecific: [
    "Key feature: a 3-syllable pickup before the first strong beat.",
    "The pickup must be fast and relatively flat.",
    "Strong syllables must be clearly highlighted.",
    "In very fast speech the pickup may reduce (‘Vil ’ke …’), but that reduction is not trained here."
  ],

  teacher: {
    focus: [
      "This exercise contains two feet, preceded by a 3-syllable anacrusis.",
      "Target rhythm: xxxXxXx.",
      "The main goal is control of the long pickup: fast, flat, tightly chained.",
      "The strong syllables must stand out tonally.",
      "In extremely rapid speech the pickup may reduce to two syllables (‘Vil ’ke Kari komme?’), but this reduction is tied to the word ‘ikke’ and is not the training goal.",
      "Segmental control remains important:",
      "• long vs short vowels",
      "• aspiration (two items in the set)",
      "• vowel–consonant chaining across word boundaries",
      "• vowel–vowel contact must stay continuous (hiatus handling)",
      "• same sound at end/start of adjacent words must behave as one long sound",
      "• assimilation where dialectally appropriate"
    ],

    actions: [
      "Pickup timing drill: teacher counts 1-2-3-CLAP (CLAP = first strong beat); students fit the pickup into 1-2-3.",
      "Stress exaggeration drill: teacher over-highlights strong syllables; students imitate.",
      "One-breath rule: entire utterance must be produced without pause.",
      "Segment spot checks: rotate attention between vowel length, aspiration, and boundary chaining.",
      
      // Extra exercises
      "Extra exercises repeat an earlier answer pattern in dialogue form.",
      "Teacher asks, students answer; then students work in pairs.",
      "Example variations:",
      "• ‘Er ikke Ali fra Irak?’ – ‘Jo, det er han.’",
      "• ‘Er ikke kaffen varm?’ – ‘Jo, det er den.’",
      "When working with present tense ‘er’, monitor /r/ + dental assimilation in dialects with apical r.",
      "Sequences like ‘er den’ may become retroflex in those dialect areas.",
      "Schwa /e/ may drop; the final nasal may also become retroflex.",
      "Dialect variation is acceptable as long as chaining and rhythm remain intact."
    ]
  }
}

,
//8A
{
  id: "8A",
  patterns: ["xxxxXx (4-syllable anacrusis + foot)"],
  examples: [
    "Hun vil ikke komme",
    "Hun kan ikke skrive"
  ],

  studentCore: [
    `hun${LINK}vil${LINK}ikke${LINK}KOMme`,
    `hun${LINK}kan${LINK}ikke${LINK}SKRIve`
  ],

  extraSpecific: [
    "Four weak syllables in the pickup: they must be fast, even, and flat.",
    "There must be no break between pickup and foot — one continuous sound chain.",
    "For the sequence /n/ + /v/ (‘hun vil’), see the earlier note in 6A: optional nasal assimilation is natural."
  ],

  teacher: {
    focus: [
      "This exercise continues work with anacrusis; the pickup now has four syllables.",
      "Target example: ‘Hun vil ikke komme’ → xxxxXx.",
      "Core requirements: rapid articulation, even intonation, and no interruption between pickup and foot.",
      "Chaining across all word boundaries must remain intact.",
      "For pronunciation of /n/ + /v/ (‘hun vil’), refer to the comment under 6A: optional assimilation is an articulatory shortcut, not an obligation."
    ],

    actions: [
      "Pickup compression drill: teacher taps 1-2-3-4-CLAP; students fit the pickup into the taps and land on the stressed syllable.",
      "Flat-intonation drill: students say the pickup almost monotone, then release into the foot.",
      "One-chain rule: full sentence must be spoken in one breath without pauses.",

      // Extra exercises
      "Extra exercises reuse the same question framework as 7B, but the answers are negative.",
      "Model answer pattern: ‘Nei, det vil hun ikke’ → X, xXxxx.",
      "Structure explanation: ‘Nei’ forms its own foot; then a 1-syllable pickup precedes the final 4-syllable foot.",
      "Important: the negation word ‘Nei’ must contain a clear diphthong.",
      "Question and answer patterns should be reused in other contexts (e.g., textbook dialogues).",
      "Teacher or students may create additional sentences that preserve the same rhythm."
    ]
  }
}

,
//8B
{
  id: "8B",
  patterns: ["xxxxXx? (same rhythm as 8A, question form)"],
  examples: [
    "Vil hun ikke komme?",
    "Kan hun ikke skrive?"
  ],

  studentCore: [
    `vil${LINK}hun${LINK}ikke${LINK}KOMme?`,
    `kan${LINK}hun${LINK}ikke${LINK}SKRIve?`
  ],

  extraSpecific: [
    "Same rhythm as 8A, now realized as a question.",
    "Four weak pickup syllables must stay fast and flat.",
    "No interruption between pickup and foot.",
    "Keep earlier segment rules active (vowel length, aspiration, chaining)."
  ],

  teacher: {
    focus: [
      "Same rhythmic pattern as 8A, now realized as a question.",
      "Target example: ‘Vil hun ikke komme?’ → xxxxXx.",
      "Students must maintain rapid articulation, even intonation, and no breaks between pickup and foot.",
      "Also monitor the segmental features trained earlier: vowel length, aspiration, and smooth chaining."
    ],

    actions: [
      "Question chaining drill: students produce the full question in one breath.",
      "Pickup timing drill: teacher taps 1-2-3-4-CLAP; students align the pickup to the taps.",
      "Segment audit: short pauses only to fix vowel-length or aspiration errors, then return to full rhythm.",

      // Extra exercises
      "Extra exercises build directly on the main 8B questions.",
      "Students now give positive answers.",
      "The answer expands the anacrusis to four syllables (xxxxXx).",
      "Known answer template: X, xXx.",
      "Pair drill: alternate question → positive answer while keeping identical rhythm."
    ]
  }
}

,
// 9A
{
  id: "9A",
  patterns: ["XxxX (light syllables inside the foot)"],
  examples: [
    "Tor lærer fransk",
    "Svein røyker pipe"
  ],

  studentCore: [
    `TOR${LINK}LÆrer${LINK}FRANSK`,
    `SVEIN${LINK}RØYker${LINK}PIPE`
  ],

  extraSpecific: [
    "Light syllables now belong inside the foot, not the pickup.",
    "This is a deliberately easier exercise — focus on sound quality.",
    "Maintain correct vowel length.",
    "Watch difficult vowels: /ø/, /ʉ/, /øy/."
  ],

  teacher: {
    focus: [
      "Work with light syllables continues, but now they belong to the foot itself.",
      "Target example: ‘Tor lærer fransk’ → XxxX.",
      "This is an easier consolidation exercise.",
      "Attention can shift toward segmental features, especially vowels.",
      "Correct vowel length must be preserved.",
      "Several difficult vowels appear: /ø/, /ʉ/, /øy/.",
      "Also present: /r/ + dental assimilation, deletion of weak /e/, and aspiration."
    ],

    actions: [
      "Vowel-length drill: exaggerate long vowels slightly without changing rhythm.",
      "Difficult-vowel isolation: practice /ø/, /ʉ/, /øy/ in short loops, then reintegrate.",
      "Assimilation awareness: demonstrate r+dental fusion once; do not overteach.",
      "Schwa-drop acceptance: allow natural deletion if rhythm remains intact.",
      "Aspiration check: quick airflow test on plosives.",

      // Extra exercises
      "Extra exercises reuse the 8B question structure.",
      "Students now give negative answers.",
      "Answer structure: X, xXxxx.",
      "Pair drill: alternate question → negative answer while preserving rhythm."
    ]
  }
}

,
//9B

{
  id: "9B",
  patterns: [
    "xxXX (two-syllable anacrusis + two 1-syllable feet)",
    "xxXxxX (two-syllable anacrusis + longer feet)"
  ],

  examples: [
    "Lærer Tor fransk?",
    "Liker Kari dessert?"
  ],

  studentCore: [
    `lærer${LINK}TOR${LINK}FRANSK?`,
    `liker${LINK}KAri${LINK}deSSERT?`
  ],

  extraSpecific: [
    "The verb sits in the pickup (anacrusis). Say it fast and flat.",
    "Even though the verb could form its own foot in other contexts, it must NOT here.",
    "After the pickup come two clear beats (two feet).",
    "Watch smooth chaining — no pauses."
  ],

  teacher: {
    focus: [
      "This exercise trains a two-syllable anacrusis followed by two feet.",
      "Target examples:",
      "‘Lærer Tor fransk?’ → xxXX (very short feet)",
      "‘Liker Kari dessert?’ → xxXxxX (longer feet)",
      "The verb belongs to the anacrusis even though it could form a foot elsewhere.",
      "The pickup must be rapid and relatively flat.",
      "Multiple opportunities appear for /r/ + dental assimilation (items 9B1, 4, 7, 8, 11)."
    ],

    actions: [
      "Pickup isolation drill: students whisper the verb, speak the feet normally.",
      "Two-beat drill: clap only on the two strong beats after the pickup.",
      "Assimilation awareness: teacher models r+dental fusion; students imitate without overthinking.",
      "No-pause rule: the full question must remain one sound chain.",

      // Extra exercises
      "Extra exercises give positive answers to the questions.",
      "The main verb from the question is repeated in the answer.",
      "Model: ‘Lærer Tor fransk?’ → ‘Ja, han lærer fransk.’",
      "Monitor assimilation where it can occur (marked in the guide).",
      "Pair drill: students alternate question → echo answer with identical rhythm."
    ]
  }
}

,
//10A
{
  id: "10A",
  patterns: ["xxxX (3-syllable anacrusis)"],

  examples: [
    "Han lærer fransk",
    "Han snakker om deg"
  ],

  studentCore: [
    `han${LINK}lærer${LINK}FRANSK`,
    `han${LINK}snakker${LINK}om${LINK}DEG`
  ],

  extraSpecific: [
    "Three light syllables in the pickup: fast, tightly chained, relatively flat.",
    "Other readings are possible, but the goal is to stabilize this rhythm.",
    "Underlined vowels are long. Diphthongs are long by definition.",
    "Complex consonant clusters must not break the pickup."
  ],

  teacher: {
    focus: [
      "The utterance begins with a 3-syllable anacrusis → xxxX.",
      "Goal: three light, tightly bound syllables with relatively flat tone.",
      "Anacrusis must be rapid — no internal pauses.",
      "Complex consonant clusters may disturb timing (items 10A5, 6, 8).",
      "Repair strategy: move an initial /s/ to the end of the previous word.",
      "Reduction case 10A8: ‘skolen’ is often pronounced without /e/ in the second syllable.",
      "The nasal becomes syllabic: [skuːln̩]. Accept this as natural speech if rhythm remains intact.",
      "Reminder: underlined vowels are long; diphthongs are inherently long."
    ],

    actions: [
      "Triple-pickup timing drill: tap 1–2–3–CLAP (CLAP = strong beat).",
      "Students fit the pickup into the taps without slowing.",
      "Cluster resilience drill: repeat sentences forbidding pauses before clusters.",
      "Reduction awareness: model careful ‘skolen’ vs reduced [skuːln̩]; rhythm must stay stable.",

      // Extra exercises
      "Extra exercises reuse the questions from 9B.",
      "In answers, the main verb is replaced with ‘å gjøre’.",
      "Model: ‘Lærer Tor fransk?’ → ‘Ja, det gjør han.’",
      "In fast speech ‘ja’ may become so light it joins the pickup.",
      "In this exercise the answer must contain TWO feet.",
      "This ‘det gjør + pronoun’ pattern is extremely frequent in Norwegian.",
      "Teach it as the default positive answer structure.",
      "Exceptions:",
      "Questions with ‘å være’: ‘Er han tysk?’ → ‘Ja, det er han.’",
      "Questions with ‘å ha’: ‘Har hun barn?’ → ‘Ja, det har hun.’",
      "Modal auxiliaries: ‘Kan de snakke tysk?’ → ‘Ja, det kan de.’",
      "In these cases the main verb is repeated."
    ]
  }
}

,
//10B

{
  id: "10B",
  patterns: ["xxxxX? (everything before final word = anacrusis)"],

  examples: [
    "Lærer han fransk?",
    "Snakker hun om deg?"
  ],

  studentCore: [
    `lærer${LINK}han${LINK}FRANSK?`,
    `snakker${LINK}hun${LINK}om${LINK}DEG?`
  ],

  extraSpecific: [
    "Everything before the final word is the pickup (anacrusis).",
    "The pickup must be fast and flat.",
    "Only the final word carries the main beat.",
    "The pitch usually rises at the end — clear question intonation."
  ],

  teacher: {
    focus: [
      "Same structural principle as 10A, but now all utterances are questions.",
      "All material before the last word belongs to the anacrusis.",
      "The anacrusis must be rapid and relatively flat.",
      "The whole utterance must carry clear question intonation.",
      "Teacher should demonstrate contrast between declarative and interrogative melody.",
      "Students reuse intonation contrasts practiced in the previous exercises."
    ],

    actions: [
      "Melody contrast drill: teacher says statement vs question using same words; students identify the difference.",
      "Final-beat drill: clap only on the last word; everything before must flow without stress.",
      "Echo drill: exaggerate rising contour → gradually naturalize it.",
      "Chain rule: no pauses anywhere before the final beat.",

      // Extra exercises
      "Extra exercise introduces an idiomatic fixed phrase:",
      "‘Ja, av og til’ → X, XxX.",
      "The rhythm pattern is already familiar.",
      "Primary version: stress both ‘av’ and ‘til’.",
      "Alternative version: stress only ‘Ja’ and ‘til’.",
      "Students create their own questions and answer with this phrase.",
      "Pair drill: rotate questions, maintain the fixed rhythm."
    ]
  }
}

,

// 11A
{
  id: "11A",
  patterns: ["XxxxxX (5-syllable foot + final foot)"],

  examples: [
    "Tor lærer ikke fransk",
    "Marit snakker ikke norsk"
  ],

  studentCore: [
    `TOR${LINK}lærer${LINK}ikke${LINK}FRANSK`,
    `MArit${LINK}snakker${LINK}ikke${LINK}NORSK`
  ],

  extraSpecific: [
    "The first foot has five syllables — weak syllables must be very fast.",
    "Tone should be flat or slightly falling through the long foot.",
    "Natural Norwegian may shorten ‘ikke’ to ’ke and drop present-tense -r.",
    "Students don’t need to produce this reduction, but must recognize it in real speech."
  ],

  teacher: {
    focus: [
      "Training continues with relatively long feet (example: ‘Tor lærer ikke fransk’ → XxxxxX).",
      "The first foot contains five syllables; unstressed syllables must be articulated rapidly.",
      "Intonation: flat or slightly falling across the long foot.",
      "Key fluency features must work together: rapid articulation, chaining, assimilation (11A1), and deletion/elision (11A5).",
      "Many native speakers reduce ‘ikke’ to ’ke, especially when the verb is monosyllabic in present tense.",
      "Present tense -r often disappears in fast speech.",
      "Students are not required to imitate these reductions, but must be aware of them for comprehension."
    ],

    actions: [
      "Five-syllable foot drill: clap only on the first stressed syllable; students compress the rest.",
      "Flat-fall melody drill: teacher models the contour; students echo.",
      "Reduction listening drill: careful vs reduced (‘ikke’ vs ’ke; -r vs no -r); students confirm meaning is unchanged.",
      "Chain integrity check: teacher listens only for pauses.",

      // Extra exercises
      "Extra exercises use questions with short 2-syllable pickups, followed by two feet.",
      "Purpose: stabilize earlier skills before harder structures.",
      "Negative answer pattern: ‘Nei, ikke nå lenger’ → X, xxXxx.",
      "Students invent new utterances using the same structure.",
      "Pair drill: question → fixed negative answer → rotate roles."
    ]
  }
}

,
//11B
{
  id: "11B",
  patterns: ["xxxxXX (long anacrusis + two feet)"],

  examples: [
    "Lærer ikke Tor fransk?",
    "Snakker ikke Marit norsk?"
  ],

  studentCore: [
    `lærer${LINK}ikke${LINK}TOR${LINK}FRANSK?`,
    `snakker${LINK}ikke${LINK}MArit${LINK}NORSK?`
  ],

  extraSpecific: [
    "The pickup is long because ‘ikke’ belongs to the anacrusis.",
    "The pickup must be fast and flat.",
    "The two following feet must be clearly marked.",
    "Produce the whole utterance as one continuous chain."
  ],

  teacher: {
    focus: [
      "A-version statements are converted into questions.",
      "The anacrusis remains long because the negation ‘ikke’ is placed in the pickup.",
      "After the pickup come two feet (example: ‘Lærer ikke Tor fransk?’ → xxxxXX).",
      "Students must clearly distinguish the fast, flat pickup from the marked feet.",
      "Maintain long vs short vowel contrasts.",
      "All utterances must be produced as a single sound chain."
    ],

    actions: [
      "Pickup vs foot contrast drill: teacher taps lightly through the pickup and claps on the two feet; students mirror timing.",
      "Chain enforcement: any pause resets the attempt.",
      "Vowel-length check: isolate long/short contrasts, then reinsert into full rhythm.",

      // Extra exercises
      "Extra exercise: students answer the questions.",
      "Positive template: ‘Jo, det gjør han’ → X, xXx.",
      "Negative template: ‘Nei, det gjør han ikke’ → X, xXxxx.",
      "In the negative pattern the final foot is longer.",
      "Pair drill: rotate question → positive answer → negative answer."
    ]
  }
}

,
//12A
{
  id: "12A",
  patterns: ["xxxxxX (five-syllable anacrusis)"],

  examples: [
    "Han lærer ikke fransk",
    "Hun kan ikke gi grøt"
  ],

  studentCore: [
    `han${LINK}lærer${LINK}ikke${LINK}FRANSK`,
    `hun${LINK}kan${LINK}ikke${LINK}GI${LINK}GRØT`
  ],

  extraSpecific: [
    "Very long pickup: five weak syllables before the beat.",
    "All available fluency tricks must be used to keep speed and chaining.",
    "Reductions and assimilations are natural and expected at this tempo.",
    "If vowels meet across a boundary, they must link smoothly."
  ],

  teacher: {
    focus: [
      "This exercise continues work with long anacruses. Rarely Norwegian allows six syllables; here students practice five.",
      "To pronounce so many syllables quickly, learners must actively use previously trained fluency strategies.",
      "The guide highlights specific fast-speech processes that may apply throughout the set:",

      "12A1: Verb-final /r/ may disappear. Even more common is reduction of ‘ikke’ → ‘ke’. These reductions may occur in all items in this exercise.",
      "12A3: The final vowel of ‘ikke’ must glide into the first vowel of ‘egg’. The vowels differ, producing a diphthong-like transition rather than two separate syllables.",
      "12A6: In fast speech /n/ in ‘hun’ may assimilate to velar [ŋ] before velars. If ‘ikke’ reduces, neighboring words may also compress further.",
      "12A9: In dialects with tapped /r/, the final /e/ in ‘desserten’ may disappear, and /r/ + /n/ assimilate to a retroflex nasal-like sound. This is dialect-dependent."
    ],

    actions: [
      "Five-tap drill: teacher taps 1-2-3-4-5-CLAP; students fit the entire pickup into the taps.",
      "Reduction listening drill: compare careful vs reduced forms (‘ikke’ vs ’ke; -r present vs dropped). Meaning must remain identical.",
      "Vowel-glide drill: slow linking practice (‘ikke‿egg’) → accelerate without glottal onset.",
      "Velar nasal demo: show optional [n] → [ŋ] before /k g/; emphasize efficiency over perfection.",
      "Dialect awareness: present ‘desserten’ variants as acceptable regional outcomes; do not require imitation.",

      // Extra exercises
      "Extra exercises also use long pickups in yes/no questions.",
      "The answer is constant across items.",
      "Teach the fixed phrase as a chunk: ‘Jo, jeg tror da det’.",
      "Students should memorize it as a ready-made conversational unit."
    ]
  }
}

,
//12B
{
  id: "12B",
  patterns: ["xxxxxX? (five-syllable anacrusis, question form)"],

  examples: [
    "Lærer han ikke fransk?",
    "Kan hun ikke gå ut?"
  ],

  studentCore: [
    `lærer${LINK}han${LINK}ikke${LINK}FRANSK?`,
    `kan${LINK}hun${LINK}ikke${LINK}gå UT?`
  ],

  extraSpecific: [
    "Same five-syllable pickup as 12A, now in question form.",
    "Pickup must stay fast, flat, and tightly chained.",
    "Initial /h/ should be light — not exaggerated.",
    "Both pronunciations of ‘skolen’ are acceptable."
  ],

  teacher: {
    focus: [
      "This exercise repeats the rhythmic structure from 12A in interrogative form.",
      "Core targets are identical: fast articulation, chaining, reduction awareness, and smooth vowel linking.",
      "Initial /h/ in pronouns must not be over-articulated. It should be present, but weak.",
      "If learners drop /h/ (as native speakers may in fast speech), prevent this from becoming a general habit.",
      "/h/ is normally pronounced in Norwegian, but under high-speed conditions it may weaken or disappear.",
      "Students must learn control — optional weakening is contextual, not random.",
      "The word ‘skolen’ has two acceptable pronunciations (full vowel vs reduced syllabic nasal form). Both are valid."
    ],

    actions: [
      "Pickup endurance drill: repeat the five-syllable anacrusis several times before releasing into the final beat.",
      "H-control drill: alternate exaggerated /h/ vs light /h/ to teach voluntary control.",
      "Question melody echo: teacher exaggerates rising contour; students imitate while preserving pickup flatness.",
      "Reduction awareness reprise: quick listening comparison of careful vs fast-speech variants.",

      // Extra exercises
      "Extra exercises use fixed positive and negative answer templates:",
      "Positive: ‘Jo, det gjør han’ → X, xXx.",
      "Negative: ‘Nei, det gjør han ikke’ → X, xXxxx.",
      "Students practice swapping subject, verb, or object while keeping the rhythm unchanged."
    ]
  }
}

,
//13A
{
  id: "13A",
  patterns: ["XxxxxxX (very long foot: 4–6 syllables)"],

  examples: [
    "Svein vil ha en sigarett",
    "Katten kan spise fisk"
  ],

  studentCore: [
    `SVEIN${LINK}vil${LINK}ha${LINK}en${LINK}sigaRETT`,
    `KATten${LINK}kan${LINK}spise FISK`
  ],

  extraSpecific: [
    "These feet are near the upper limit of normal spoken Norwegian.",
    "After the first stressed syllable, keep everything flat and fast.",
    "Do not insert pauses inside the long foot."
  ],

  teacher: {
    focus: [
      "This exercise returns focus to feet rather than anacruses.",
      "Students practice feet containing 4–6 syllables (example: ‘Svein vil ha en sigarett’ → XxxxxxX).",
      "This approaches the outer boundary of what is typical in natural spoken Norwegian.",
      "Core targets remain identical to earlier work:",
      "• chaining of all syllables",
      "• natural deletion/reduction where appropriate",
      "• flat or slightly falling tone after the stressed syllable",
      "Learners must resist inserting secondary stresses or pauses inside the long foot."
    ],

    actions: [
      "Wave drill: students imagine one continuous gesture from the first stress to the end of the foot.",
      "Metronome drill: one strong beat followed by compressed trailing syllables.",
      "Pause policing: teacher stops production immediately if a micro-pause appears.",
      "Compression ladder: repeat the same utterance gradually faster without changing rhythm shape.",

      // Extra exercises
      "Extra exercises return to a slightly simpler question structure to stabilize earlier rhythms.",
      "Answers begin with stressed ‘Ja’ followed by a three-syllable anacrusis.",
      "Students alternate question → answer chains while preserving rhythm."
    ]
  }
}

,
// 13B
{
  id: "13B",
  patterns: ["xXxxxxX (question form; slightly shorter feet than 13A)"],

  examples: [
    "Vil Svein ha en sigarett?",
    "Vil katten spise fisk?"
  ],

  studentCore: [
    `vil${LINK}SVEIN${LINK}ha${LINK}en${LINK}sigaRETT?`,
    `vil${LINK}KATten${LINK}spise FISK?`
  ],

  extraSpecific: [
    "Same skills as 13A, now in question form.",
    "After the first stressed syllable, keep the chain flat and compressed.",
    "By now you should be able to name what you are training (pickup vs foot, chaining, vowel length)."
  ],

  teacher: {
    focus: [
      "This exercise consolidates previously learned skills by converting 13A utterances into questions.",
      "Feet become slightly shorter (example: ‘Vil Svein ha en sigarett?’ → xXxxxxX).",
      "The technical focus is unchanged: chaining, reduction, and flat/slightly falling tone after the stressed syllable.",
      "After completing roughly 2/3 of the book, students should be able to identify the central training targets themselves.",
      "Meta-awareness becomes part of the training: students should know what they are controlling."
    ],

    actions: [
      "Self-diagnosis drill: before speaking, students state the target (‘long foot’, ‘no pauses’, etc.).",
      "Peer monitoring: partners listen only for breaks or added stresses.",
      "Question contour overlay: add final rise without disturbing the internal rhythm.",

      // Extra exercises
      "Extra exercises answer the A-questions using echo-style responses.",
      "Model: ‘Må Tor lære fransk?’ → ‘Ja, han må lære fransk.’",
      "Explain: this is less typical in natural conversation (more natural would be ‘Ja, det må han’),",
      "but the chosen form is used because it contains a relatively long anacrusis for training.",
      "After mastery, optionally contrast with the shorter idiomatic answers."
    ]
  }
}

,
//14 A
{
  id: "14A",
  patterns: ["xxxxxxXx (long anacrusis; target pattern only)"],

  examples: [
    "Han vil kjøpe sigaretter",
    "Hun vil kjøpe sjokolade"
  ],

  studentCore: [
    `han${LINK}vil${LINK}kjøpe${LINK}sigaRETTER`,
    `hun${LINK}vil${LINK}kjøpe${LINK}sjoKOLADE`
  ],

  extraSpecific: [
    "Target rhythm is a long pickup — do NOT fall back to earlier patterns.",
    "Everything before the final foot must stay fast and flat.",
    "If the longest sentences are hard, master the shorter ones first."
  ],

  teacher: {
    focus: [
      "This exercise returns to very long anacruses (example: ‘Han vil kjøpe sigaretter’ → xxxxxxXx).",
      "Teacher must emphasize that THIS rhythm is the goal — not the earlier pattern (xxXxxxXx) that students already know.",
      "Students will naturally drift toward the familiar rhythm; this must be corrected.",
      "Utterances vary in length. If the longest ones cause difficulty, begin with the shortest and scale up.",
      "Core priorities remain: chaining and rapid articulation of weak syllables."
    ],

    actions: [
      "Contrast modeling: teacher produces target rhythm vs the older familiar rhythm; students identify which is correct.",
      "Short-first progression: stabilize shorter items before introducing longer ones.",
      "Pickup compression drill: speak the entire pickup as one blur, then land firmly on the final beat.",
      "Immediate correction rule: any extra stress inside the pickup resets the attempt.",

      // Extra exercises
      "Extra exercises return to an earlier stabilized answer pattern.",
      "Answer structure: stressed ‘Ja’ + pause + 1-syllable pickup + 2-syllable foot → X, xXx.",
      "Students alternate question → fixed rhythmic answer chains."
    ]
  }
},
//14 B
{
  id: "14B",
  patterns: ["xxxxxXx (long anacrusis in questions)"],

  examples: [
    "Vil du ha sjokolade?",
    "Kan Gro sitte på stolen?"
  ],

  studentCore: [
    `vil${LINK}du${LINK}ha${LINK}sjoKOLADE?`,
    `kan${LINK}Gro${LINK}sitte${LINK}på${LINK}STOlen?`
  ],

  extraSpecific: [
    "Long pickup again — don’t split it into smaller beats.",
    "Everything before the final foot must stay fast and flat.",
    "Only the last foot carries the main rhythm."
  ],

  teacher: {
    focus: [
      "Long anacruses now appear in question form (example: ‘Vil du ha sjokolade?’ → xxxxxXx).",
      "Students tend to split a long pickup into a shorter pickup + mini-foot; this must be prevented.",
      "Teacher may practice those shorter components separately, then reintegrate them into the full pattern.",
      "Dialogues from textbooks can be used as material, but they contain mixed rhythm patterns.",
      "Unlike this controlled exercise sequence, textbooks are not rhythm-engineered; expect variation.",
      "These drills gradually build rhythmic competence that textbooks do not scaffold explicitly.",
      "Textbooks often contain easier rhythm patterns than the late exercises here."
    ],

    actions: [
      "Anti-splitting drill: teacher claps only once — on the final foot; any earlier beat resets the attempt.",
      "Component practice: rehearse short pickup + short foot separately, then merge into the long pickup.",
      "Dialogue adaptation: force textbook lines into the target rhythm for controlled training.",
      "Pickup stretch drill: exaggerate the length of the pickup without inserting stress.",
      "Group landing drill: whole class must land the final beat simultaneously.",

      // Extra exercises
      "Extra exercises train short answers to the questions.",
      "Template: ‘Ja, det kan hun’ → X, xXx.",
      "Stress falls on ‘Ja’ and the modal auxiliary.",
      "Students rotate modal verbs while keeping identical rhythm."
    ]
  }
}

,
//15A
{
  id: "15A",
  patterns: ["XxxxxX (expanded long foot with weak particle)"],

  examples: [
    "Tor må jo lære fransk",
    "Gro kan jo kjøpe kaffe"
  ],

  studentCore: [
    `TOR${LINK}må${LINK}jo${LINK}lære${LINK}FRANSK`,
    `GRO${LINK}kan${LINK}jo${LINK}kjøpe${LINK}KAFFE`
  ],

  extraSpecific: [
    "The extra word ‘jo’ adds length but must stay weak.",
    "Particles like jo / nok / da / vel never take stress here.",
    "The long foot must still sound like one continuous chain."
  ],

  teacher: {
    focus: [
      "This exercise builds on 13A but inserts an additional weak syllable (‘jo’), lengthening the first foot: XxxxxX.",
      "Chaining and intonation remain central targets.",
      "Teacher may explain the pragmatic function of particles such as ‘jo, nok, da, vel’.",
      "These words are always unstressed in this pattern but play an important role as modifiers.",
      "There exists a parallel series of stressed forms with different meanings; do not confuse them.",
      "Students must not promote ‘jo’ to a new rhythmic beat."
    ],

    actions: [
      "Particle invisibility drill: students must say ‘jo’ without creating a new beat.",
      "Stacking drill: insert jo / nok / da while preserving the same rhythm.",
      "Meaning awareness: teacher contrasts sentences with vs without particles; discuss nuance.",
      "Compression drill: lengthen the foot but forbid additional stress.",
      "Chain enforcement: full utterance must remain one prosodic unit.",

      // Extra exercises
      "Extra exercises use short negative answers.",
      "Model: ‘Nei, ikke nå’ → X, xxX.",
      "Answers reflect the modal auxiliaries used in the questions.",
      "Students rotate modal verbs but keep identical rhythm."
    ]
  }
}

,
//15B

{
  id: "15B",
  patterns: ["xxxXxxX (pickup + two short feet, question form)"],

  examples: [
    "Må ikke Tor lære fransk?",
    "Kan ikke Gro kjøpe kaffe?"
  ],

  studentCore: [
    `må${LINK}ikke${LINK}TOR${LINK}lære${LINK}FRANSK?`,
    `kan${LINK}ikke${LINK}GRO${LINK}kjøpe${LINK}KAFFE?`
  ],

  extraSpecific: [
    "The long foot from 15A splits into pickup + two feet in the question.",
    "Vowel meetings across word boundaries must stay continuous — no pauses.",
    "Keep the contrast between sj-sound and kj-sound clear (sjokolade vs kino)."
  ],

  teacher: {
    focus: [
      "The long foot in 15A divides into an anacrusis and two shorter feet when the declarative becomes a question: xxxXxxX.",
      "Students have observed this rhythm shift many times; emphasize that rhythm is not tied to sentence type, but to focus structure.",
      "This exercise contains several vowel–vowel meetings across word boundaries (items 1–4); ensure continuous sound flow with no glottal breaks.",
      "Vowel deletion appears in some items (5, 9, 10) and assimilation in item 1; these should now be mastered automatically.",
      "Segmental contrast target: students must distinguish /ʃ/ in ‘sjokolade’ from /ç/ in ‘kino’."
    ],

    actions: [
      "Hiatus drill: slow vowel linking across boundaries → speed up without breaks.",
      "Deletion tolerance: allow reduced vowels if rhythm remains intact.",
      "Assimilation check: quick echo of the marked items.",
      "Sound contrast drill: rapid alternation ‘sjokolade / kino’ focusing on tongue position.",
      "Rhythm explanation moment: map focus → rhythm, not question vs statement.",

      // Extra exercises
      "Extra exercises repeat the main questions and answer them negatively.",
      "Answer template: X, xXxxx (main verb repeated, followed by weak syllables).",
      "Pair drill: ask → negative answer → swap roles."
    ]
  }
}

,
//16A

{
  id: "16A",
  patterns: ["xxxxxX (5-syllable anacrusis, declarative)"],

  examples: [
    "Han må jo lære fransk",
    "Hun bør nok kjøpe kaffe"
  ],

  studentCore: [
    `han${LINK}må${LINK}jo${LINK}lære${LINK}FRANSK`,
    `hun${LINK}bør${LINK}nok${LINK}kjøpe${LINK}KAFFE`
  ],

  extraSpecific: [
    "Five weak syllables in the pickup: fast, flat, tightly chained.",
    "Particles (jo / nok / vel / da) stay weak even when stacked.",
    "Different rhythm patterns change meaning — listen for the contrast."
  ],

  teacher: {
    focus: [
      "Continues training of long anacruses: five syllables before the main beat (xxxxxX).",
      "The same pronunciation principles apply as in earlier exercises.",
      "These structures are not very frequent in Norwegian, but they do occur.",
      "Students must maintain the specified rhythm even though alternative readings are possible.",
      "Teacher may demonstrate alternative patterns:",
      "• ‘Han må jo lære fransk’ → XxxxxX",
      "• ‘Han må jo lære fransk’ → xxxxXx",
      "Students should understand how rhythm changes meaning.",
      "Assimilation note: for /n/ + /v/ sequences (‘vil hun være’), see 6A."
    ],

    actions: [
      "Meaning contrast drill: teacher produces the same sentence with different stress placement; students describe the change in meaning.",
      "Pickup compression drill: repeat long anacrusis at increasing speed without adding beats.",
      "Particle stacking drill: insert jo / vel / nok while keeping rhythm unchanged.",
      "Echo practice: students mirror teacher rhythm exactly before naturalizing tempo.",

      // Extra exercises
      "Extra exercises: yes/no questions with 4–6 syllables in the anacrusis.",
      "Start with the shortest patterns first.",
      "If difficult, scaffold with shorter pickups:",
      "• ‘Går han ut?’",
      "• ‘Lærer han fransk?’",
      "• ‘Vil hun gå ut?’",
      "• ‘Vil hun lære fransk?’",
      "All questions receive the same answer:",
      "‘Selvfølgelig må han det’ → xXxxxxX.",
      "Teach the answer as a fixed rhythmic phrase."
    ]
  }
}
,

//16B

{
  id: "16B",
  patterns: ["xxxxxxX (6-syllable anacrusis; stress-test pattern)"],

  examples: [
    "Må han ikke lære fransk?",
    "Kan hun ikke kjøpe kaffe?"
  ],

  studentCore: [
    `må${LINK}han${LINK}ikke${LINK}lære${LINK}FRANSK?`,
    `kan${LINK}hun${LINK}ikke${LINK}kjøpe${LINK}KAFFE?`
  ],

  extraSpecific: [
    "Six weak syllables in the pickup — this is an extreme test.",
    "Everything must stay fast, flat, and tightly chained.",
    "If you can do this, normal Norwegian rhythm feels easy."
  ],

  teacher: {
    focus: [
      "This exercise deliberately pushes students to the limit.",
      "The example contains a six-syllable anacrusis: xxxxxxX.",
      "Speech must remain rapid, tightly chained, and tonally flat.",
      "Producing such rare patterns implies mastery of all shorter pickups.",
      "Intonation must still signal function clearly.",
      "Chaining, deletion, and assimilation rules apply exactly as before.",
      "Students should notice how meaning changes when rhythm changes."
    ],

    actions: [
      "Extreme pickup drill: teacher silently counts six beats; class lands together on the final stress.",
      "No-breath rule: entire utterance in one breath — any reset means retry.",
      "Meaning-switch demo: change rhythm and discuss pragmatic shift.",
      "Pair endurance drill: repeat until rhythm stabilizes under speed.",
      "Celebrate success: when pairs manage this reliably, reward the class.",

      // Extra exercises
      "Extra exercises reuse the same questions and attach an equally complex answer:",
      "‘Det er klart at han må det!’ → xxxxxxX.",
      "Teach the answer as a fixed rhythmic phrase.",
      "Pair drill: question → answer → swap roles.",
      "If students can do this consistently, the teacher should celebrate with bløtkake."
    ]
  }
}
,

//17A
{
  id: "17A",
  patterns: ["XxxxxXx (stable multi-foot chain)"],

  examples: [
    "Anne ligger og leser",
    "Sølvi sitter og spiser",
    "Barna er ute og leker"
  ],

  studentCore: [
    `ANne${LINK}ligger${LINK}og${LINK}LEser`,
    `SØLvi${LINK}sitter${LINK}og${LINK}SPIser`,
    `BARna${LINK}er${LINK}ute${LINK}og${LINK}LEker`
  ],

  extraSpecific: [
    "This rhythm should now feel manageable.",
    "Focus on flow and natural chaining.",
    "Changing stress changes meaning."
  ],

  teacher: {
    focus: [
      "After the extreme patterns of chapter 16, this exercise steps back to a rhythm students should now control.",
      "The rhythm itself should not be difficult; attention can shift to fluency features.",
      "Students should focus on chaining, vowel length, reduction, and smooth intonation.",
      "The expressions describe ongoing actions and body position (‘ligger’, ‘sitter’, etc.), which are common in Norwegian.",
      "Stress placement affects meaning: stressing the verb creates contrast (‘Anne LIGGER og leser’ implies she is not standing)."
    ],

    actions: [
      "Fluency polish drill: students aim for smooth, natural tempo instead of survival mode.",
      "Contrast stress drill: teacher shifts stress; students interpret the implied contrast.",
      "Flow rehearsal: repeat the chain until it feels like one physical gesture.",
      "Roleplay: students act out the verbs (lie/sit/stand) while speaking to reinforce meaning–prosody link.",

      // Extra exercises
      "Extra exercises intentionally revisit earlier rhythm patterns.",
      "Answers contain mixed rhythmic structures to test retention.",
      "Teacher listens for regression after the difficulty spike in chapter 16."
    ]
  }
}
,

//17B
{
  id: "17B",
  patterns: ["xxXxxXx (question form, repeated earlier pattern)"],

  examples: [
    "Ligger Anne og leser?",
    "Sitter Sølvi og spiser?",
    "Er barna ute og leker?"
  ],

  studentCore: [
    `ligger${LINK}ANne${LINK}og${LINK}LEser?`,
    `sitter${LINK}SØLvi${LINK}og${LINK}SPIser?`,
    `er${LINK}BARna${LINK}ute${LINK}og${LINK}LEker?`
  ],

  extraSpecific: [
    "Same rhythm as 17A, but with question intonation.",
    "Keep the chain continuous.",
    "Answers are longer than the questions."
  ],

  teacher: {
    focus: [
      "This is the interrogative version of 17A and repeats an earlier rhythmic pattern.",
      "Several cases involve /r/-assimilation (17B2, 5, 6, 10, 12).",
      "Apply assimilation in dialects where it is natural.",
      "Ensure /r/ is clearly pronounced when it occurs at the end of the utterance.",
      "Maintain continuous chaining and correct vowel length."
    ],

    actions: [
      "Assimilation awareness drill: compare dialect versions with and without /r/-assimilation.",
      "End-consonant check: exaggerate final /r/ so students feel the closure.",
      "Question melody rehearsal: exaggerate rising intonation first, then naturalize.",
      "Chain integrity drill: teacher interrupts only when flow breaks.",

      // Extra exercises
      "Answers contain long pickups (5–6 syllables):",
      "‘Hvorfor spør du om det?’",
      "‘Hvorfor lurer du på det?’",
      "Students practice using these as natural conversational responses.",
      "Roleplay: students must deploy answers in appropriate contexts.",
      "Goal: responses should sound automatic and situationally appropriate."
    ]
  }
}
,

//18A
{
  id: "18A",
  patterns: ["xxxxXx (long stable foot)"],

  examples: [
    "Den ligger og sover",
    "Hun sitter og spiser",
    "De er ute og leker"
  ],

  studentCore: [
    `den${LINK}ligger${LINK}og${LINK}SOver`,
    `hun${LINK}sitter${LINK}og${LINK}SPIser`,
    `de${LINK}er${LINK}ute${LINK}og${LINK}LEker`
  ],

  extraSpecific: [
    "‘og’ is pronounced /o/ — the <g> is silent.",
    "Do not let consonant clusters break the rhythm.",
    "This rhythm should now feel natural and stable."
  ],

  teacher: {
    focus: [
      "Final chapters focus on long feet; here the foot has four weak syllables: xxxxXx.",
      "Students should now be able to produce this without major difficulty.",
      "Ensure smooth chaining and compression of weak syllables.",
      "Watch that complex consonant sequences do not interrupt flow.",
      "Monitor other silent letters (e.g. ‘det’, ‘ferdig’).",
      "Critical detail: ‘og’ must be pronounced /o/, with silent <g>."
    ],

    actions: [
      "Silent-letter drill: exaggerate spelling vs natural pronunciation → choose natural.",
      "Cluster smoothing: repeat problematic clusters without stopping rhythm.",
      "Flow audit: teacher listens only for breaks in the chain.",
      "Compression ladder: gradually increase tempo while preserving structure.",

      // Extra exercises
      "Extra exercises reuse the same rhythmic base in question form: xxxxXx.",
      "Pickup must stay fast, flat, and tightly chained.",
      "Entire utterance carries question intonation.",
      "Answers alternate between two common Norwegian patterns:",
      "‘Nei, det tror jeg ikke’",
      "‘Ja, det tror jeg’",
      "Students rotate positive/negative answers in pair drills.",
      "Goal: automatic conversational fluency."
    ]
  }
}
,

//18B

{
  id: "18B",
  patterns: ["xxxxXx (question repetition of 18A)"],

  examples: [
    "Ligger hun og sover?",
    "Sitter hun og spiser?",
    "Er de ute og leker?"
  ],

  studentCore: [
    `ligger${LINK}hun${LINK}og${LINK}SOver?`,
    `sitter${LINK}hun${LINK}og${LINK}SPIser?`,
    `er${LINK}de${LINK}ute${LINK}og${LINK}LEker?`
  ],

  extraSpecific: [
    "Same rhythm as 18A, but with question intonation.",
    "All earlier skills must now operate automatically.",
    "This stage is about stability, not new material."
  ],

  teacher: {
    focus: [
      "The rhythm from 18A is repeated in question form: xxxxXx.",
      "Maintain all previously introduced skills simultaneously.",
      "This is a good moment to compare early recordings with current speech.",
      "Students should hear clear improvement in rhythm and segment control.",
      "Variants with back /r/ are intentionally excluded in this exercise.",
      "Focus is consolidation, not expansion."
    ],

    actions: [
      "Before/after playback: students evaluate their own progress.",
      "Self-diagnosis drill: students identify what improved.",
      "Full-chain rehearsal: no pauses allowed anywhere.",
      "Teacher listens for regression to earlier habits.",

      // Extra exercises
      "Students practice answering the questions.",
      "Two response patterns:",
      "‘Nei, det tror jeg ikke’ → X, xXxxx",
      "‘Ja, det tror jeg’ → X, xXx",
      "Both should feel easy at this stage.",
      "Practice /r/+d assimilation in dialects where natural.",
      "Goal: responses sound conversational and confident."
    ]
  }
}
,

// 19A

{
  id: "19A",
  patterns: ["XxxxxxxX (very long foot)"],

  examples: [
    "Anne ligger og leser fransk",
    "Sølvi sitter og spiser middag",
    "Barna er ute og leker gjemsel"
  ],

  studentCore: [
    `ANne${LINK}ligger${LINK}og${LINK}leser${LINK}FRAnsk`,
    `SØLvi${LINK}sitter${LINK}og${LINK}spiser${LINK}MIDdag`,
    `BARna${LINK}er${LINK}ute${LINK}og${LINK}LEker${LINK}GJEMsel`
  ],

  extraSpecific: [
    "This is one of the longest feet in the program.",
    "If you can do this, normal Norwegian rhythm is easy.",
    "Everything must stay chained."
  ],

  teacher: {
    focus: [
      "Students now work with extremely long feet: XxxxxxxX.",
      "Such constructions are rare but do occur in natural Norwegian.",
      "Successful execution demonstrates mastery of most Norwegian rhythm patterns.",
      "Correct articulation depends on previously trained motor patterns.",
      "All prior elements must operate simultaneously:",
      "chaining, assimilation, reduction, vowel length, aspiration, rounding."
    ],

    actions: [
      "Full-chain endurance drill: say the entire foot without micro-pauses.",
      "Motor memory rehearsal: repeat until movement feels automatic.",
      "Backward build: start from final stress and expand left.",
      "Forward build: add one syllable at a time while preserving flow.",

      // Extra exercises
      "Extra exercises are intentionally easier than the main task.",
      "Question forms contain shorter pickups and feet.",
      "Answers include a four-syllable pickup reflecting subject + verb.",
      "Teacher monitors possible assimilation points.",
      "Goal: stability under reduced difficulty."
    ]
  }
}
,

//19B

{
  id: "19B",
  patterns: ["xxXxxxxX (short pickup + long foot + short foot)"],

  examples: [
    "Ligger Anne og leser fransk?",
    "Sitter Sølvi og spiser middag?",
    "Er barna ute og leker gjemsel?"
  ],

  studentCore: [
    `ligger${LINK}ANne${LINK}og${LINK}leser${LINK}FRAnsk?`,
    `sitter${LINK}SØLvi${LINK}og${LINK}spiser${LINK}MIDdag?`,
    `er${LINK}BARna${LINK}ute${LINK}og${LINK}LEker${LINK}GJEMsel?`
  ],

  extraSpecific: [
    "Build the sentence in pieces if needed.",
    "Always return to the full chain.",
    "The full version must feel like one gesture."
  ],

  teacher: {
    focus: [
      "This exercise steps slightly back before the final extreme patterns.",
      "Structure: short pickup + long foot + short foot.",
      "Correct articulation emerges when all sub-skills operate together.",
      "If difficult, build the utterance progressively forward or backward.",
      "Each fragment must keep the same rhythm it has inside the full sentence.",
      "Teacher must model the complete version first."
    ],

    actions: [
      "Backward chaining drill:",
      "fransk → leser fransk → og leser fransk → Anne og leser fransk → full sentence.",
      "Forward chaining drill:",
      "Ligger → Ligger Anne → Ligger Anne og → etc.",
      "Students repeat each stage after the teacher.",
      "Always return to the complete utterance.",
      "No rhythm change allowed in fragments.",

      // Extra exercises
      "Answers are rhythmically complex:",
      "‘Selvfølgelig gjør hun ikke det’ → xXxxxxxxX.",
      "Mark the main stress clearly.",
      "Remaining syllables compress with falling tone.",
      "Utterance ends with a strong stressed syllable.",
      "Goal: maintain structure under long compression."
    ]
  }
}
,

//20A

{
  id: "20A",
  patterns: ["xxxxxxX (extreme long pickup)"],

  examples: [
    "Hun ligger og leser fransk",
    "Hun sitter og spiser middag",
    "De er ute og leker gjemsel"
  ],

  studentCore: [
    `hun${LINK}ligger${LINK}og${LINK}leser${LINK}FRAnsk`,
    `hun${LINK}sitter${LINK}og${LINK}spiser${LINK}MIDdag`,
    `de${LINK}er${LINK}ute${LINK}og${LINK}LEker${LINK}GJEMsel`
  ],

  extraSpecific: [
    "This is the longest pickup in the program.",
    "Fast, flat, no breaks.",
    "Think of it as one breath."
  ],

  teacher: {
    focus: [
      "Chapter 20 contains the most complex patterns in the course.",
      "Students now handle very long pickups requiring strong concentration.",
      "The entire course has gradually built toward this point.",
      "These exercises function as a natural culmination of training.",
      "If articulation breaks down, reduce the pickup length temporarily.",
      "Alternatively, build the pickup word-by-word."
    ],

    actions: [
      "Pickup ladder: shorten → stabilize → extend again.",
      "Word-by-word construction drill.",
      "Breath control rehearsal: single exhalation delivery.",
      "Flat-tone rehearsal: prevent premature stress.",

      // Extra exercises
      "Six-syllable pickup must remain fast and chained.",
      "No pauses allowed.",
      "Tone stays flat until the foot.",
      "Answers use a reusable conversational template.",
      "Responses reflect subject + verb of the question.",
      "Goal: automatic long-chain control."
    ]
  }
}
,

//20B

{
  id: "20B",
  patterns: ["xxxxxxX (extreme long pickup, question form)"],

  examples: [
    "Ligger hun og leser fransk?",
    "Sitter hun og spiser middag?",
    "Er de ute og leker gjemsel?"
  ],

  studentCore: [
    `ligger${LINK}hun${LINK}og${LINK}leser${LINK}FRAnsk?`,
    `sitter${LINK}hun${LINK}og${LINK}spiser${LINK}MIDdag?`,
    `er${LINK}de${LINK}ute${LINK}og${LINK}LEker${LINK}GJEMsel?`
  ],

  extraSpecific: [
    "Same extreme pickup as 20A, but now in question form.",
    "If needed: build the chain gradually, exactly like 20A.",
    "Overtraining here makes normal speech easy."
  ],

  teacher: {
    focus: [
      "Rhythm identical to 20A: six-syllable pickup before the foot.",
      "Such long pickups are rare in real Norwegian.",
      "If students master this, they are effectively overtrained.",
      "Everyday Norwegian uses shorter pickups than this.",
      "Goal: automatic control of compression, chaining, and tone."
    ],

    actions: [
      "Reuse 20A buildup strategy if articulation collapses.",
      "Backwards chaining drill.",
      "Tempo escalation: slow → natural → fast.",
      "Question intonation without breaking rhythm.",

      // Extra exercises
      "Answers extend the question into a full phrase.",
      "Model answer: ‘Nei, hun er visst ferdig nå’.",
      "Pattern: X, xxxXxx.",
      "Answer must mirror subject and verb.",
      "Practice automatic conversational reuse."
    ]
  }
}
,



];